import clsx from 'clsx';
import _ from 'lodash';
import { Field, useForm } from 'react-final-form';
import { FormError } from '@/core/FormError.atom';
import { SelectIdentity } from '@/core/SelectIdentity.molecule';
import { FormFieldWrapper } from '@/formbuilder/FormFieldWrapper';
import {
  getFormFieldProps,
  getFormFieldWrapperProps,
  getValidationFunction,
} from '@/formbuilder/formbuilder.utilities';
import type { IdentityOption } from '@/core/SelectIdentity.molecule';
import type { ValidatingFormComponent } from '@/formbuilder/formBuilder.types';
import type { IdentityPreviewV1 } from '@seeqdev/api/model/IdentityPreviewV1';

export interface SelectIdentityProps extends ValidatingFormComponent<IdentityOption | IdentityOption[]> {
  component: 'SelectIdentityFormComponent';
  name: string;
  placeholder?: string;
  unauthorizedTooltip?: string;
  idForLabel?: string;
  allowGroups?: boolean;
  showDirectory?: boolean;
  includeAllProperties?: boolean;
  startEditable?: boolean;
  allowEditing?: boolean;
  multiple?: boolean;
  clearIdentityWhenEmpty?: boolean;
  allowNew?: boolean;
  skipIdentity?: string | null;
  label?: string;
  labelIcon?: string;
  labelIconAction?: () => void;
  labelIconTooltip?: string;
  labelClassNames?: string;
  labelIconClassNames?: string;
  removeEditIcon?: boolean;
}

export const SelectIdentityFormComponent = (props: SelectIdentityProps) => {
  const {
    idForLabel,
    allowGroups,
    showDirectory,
    includeAllProperties,
    startEditable,
    allowEditing,
    placeholder,
    extraClassNames,
    tooltip,
    unauthorizedTooltip,
    multiple,
    testId = 'selectIdentity',
    name,
    extendValidation,
    validation,
    customErrorText,
    value,
    skipToFormState,
    clearIdentityWhenEmpty,
    allowNew,
    skipIdentity,
    label,
    labelIcon,
    labelIconAction,
    labelIconTooltip,
    labelIconClassNames,
    labelClassNames,
    removeEditIcon,
  } = props;

  const formState = useForm().getState();
  const showError = _.has(formState.errors, name) && (_.has(formState.dirtyFields, name) || formState.submitFailed);

  const defaultValidation = (value: Partial<IdentityPreviewV1>) => !!(multiple ? value?.id : !value?.id);

  const appliedValidation = getValidationFunction<any>(defaultValidation, extendValidation, validation);

  return (
    <FormFieldWrapper
      {...getFormFieldWrapperProps(props)}
      label={label ? label : undefined}
      labelIcon={labelIcon}
      labelIconAction={labelIconAction}
      labelIconTooltip={labelIconTooltip}
      labelIconClassNames={labelIconClassNames}
      labelClassNames={labelClassNames}>
      <div className={clsx('flexRowContainer', extraClassNames)}>
        <Field name={name} validate={appliedValidation}>
          {({ input, meta }) => {
            const formFieldProps = getFormFieldProps(formState, input, meta, props);

            return (
              <SelectIdentity
                setIdentity={formFieldProps.onChange}
                identity={skipToFormState ? input.value : value}
                placeholder={placeholder}
                tooltip={tooltip}
                unauthorizedTooltip={unauthorizedTooltip}
                idForLabel={idForLabel}
                allowGroups={allowGroups}
                showDirectory={showDirectory}
                includeAllProperties={includeAllProperties}
                startEditable={startEditable}
                allowEditing={allowEditing}
                multiple={multiple}
                isInvalid={showError}
                clearIdentityWhenEmpty={clearIdentityWhenEmpty}
                allowNew={allowNew}
                skipIdentity={skipIdentity}
                removeEditIcon={removeEditIcon}
              />
            );
          }}
        </Field>
        {showError && customErrorText && <FormError errorText={customErrorText} dataTestId={`${testId}Error`} />}
      </div>
    </FormFieldWrapper>
  );
};
